import { useCallback, useEffect, useRef, useState } from 'react'

import { useFileEditors } from '@/hooks/files/useFileEditors'
import {
  saveEditorEditsFirst,
  useFileVersions,
  useForgetHeldCopies,
} from '@/hooks/files/useFileVersions'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { FileVersionList } from '@/types/file/FileVersion'
import type { UUID } from '@/types/Id'

interface UsePreviewFreshnessArgs {
  fileId: UUID
  /** The lists the file sits in, so they show its new size and modified time. */
  pursuitId?: UUID | ''
  /** Which version the document on screen was downloaded as, or null where it was taken before the file kept a history. */
  heldVersionId: string | null
  /** Off while an older version is being read: a version is settled, so nothing can make it newer. */
  enabled: boolean
}

interface PreviewFreshness {
  /** A version was saved after the document on screen was downloaded. */
  outdated: boolean
  showLatest: () => void
}

/**
 * Whether the copy in hand is older than the head of the history — which the two
 * simply differing does not say. The history and the bytes are read independently,
 * and a browser writes its own save into the copy it holds before asking for the
 * list again: the copy is then ahead of the list rather than behind it.
 */
const behindHead = (
  heldVersionId: string | null,
  versions: FileVersionList,
): boolean => {
  const head = versions.current_version_id
  if (!head || head === heldVersionId) {
    return false
  }
  // Bytes taken before the file kept a history, so every version came after them.
  if (heldVersionId === null) {
    return true
  }
  // A version the list has never heard of is newer than all of it, and the list is
  // what is behind: the head only ever moves forward, and versions are not dropped.
  return versions.data.some((version) => version.id === heldVersionId)
}

/**
 * Whether the document on screen is still the file, and how to catch it up.
 *
 * Making a version out of what an open editor holds is tens of seconds for a
 * proposal, so the bytes go up first and the save is asked for behind them — and
 * not at all while somebody is in the editor, where the version would be a
 * half-finished sentence and the export costs most. That save answers here before
 * the list has been asked again, which is why it counts alongside the list.
 */
export const usePreviewFreshness = ({
  fileId,
  pursuitId = '',
  heldVersionId,
  enabled,
}: UsePreviewFreshnessArgs): PreviewFreshness => {
  const { accessToken } = useAuthInfoWithThrow()
  const { data: editors, isSuccess } = useFileEditors({ fileId, enabled })
  const { data: versions } = useFileVersions({ fileId, enabled })
  const forgetHeldCopies = useForgetHeldCopies({ pursuitId })

  // Which file a save landed for, rather than a flag plus an effect to clear it.
  const [savedFileId, setSavedFileId] = useState<UUID | null>(null)

  // Once per file, and once more each time its room empties. A room already saved
  // only answers `unchanged`, at the cost of another read of the whole document;
  // a room somebody has just left holds work nothing else here has asked to save.
  const askedFor = useRef<UUID | null>(null)
  const wasOccupied = useRef(false)
  useEffect(() => {
    if (!enabled || !isSuccess) {
      return
    }
    if (editors !== null) {
      wasOccupied.current = true
      return
    }
    if (wasOccupied.current) {
      wasOccupied.current = false
      askedFor.current = null
    }
    if (askedFor.current === fileId) {
      return
    }
    askedFor.current = fileId

    let reading = true
    void saveEditorEditsFirst(fileId, accessToken).then((outcome) => {
      if (reading && outcome === 'saved') {
        setSavedFileId(fileId)
      }
    })
    return () => {
      reading = false
    }
  }, [enabled, isSuccess, editors, fileId, accessToken])

  const showLatest = useCallback(() => {
    setSavedFileId(null)
    void forgetHeldCopies(fileId)
  }, [forgetHeldCopies, fileId])

  // A version made anywhere else — another tab, another person, the save that runs
  // when the last editor leaves — reaches this one only as the head of the history
  // moving off the copy in hand. The list refetches when the window comes back;
  // the downloaded bytes are held forever and never do.
  const behind = enabled && !!versions && behindHead(heldVersionId, versions)

  return { outdated: behind || savedFileId === fileId, showLatest }
}
